import React, { useCallback, useEffect, useState } from "react";
import { MovmoCheckoutButton, consumeMovmoCheckoutReturn } from "@movmo/express-checkout-button";
import "@movmo/express-checkout-button/dist/index.css";

import { buildMovmoFlightOfferForClic } from "../../services/mockOffers";
import { useMockMode } from "../../hooks/useMockMode";
import "./ClicDemo.css";

// Screenshot-based demo of Clic Air's booking flow (MOVMO-511, KIU pilot).
//
// Unlike the other airline demos this is not a coded replica: each step is a
// full-page screenshot of Clic's real checkout (exported from the Figma
// master, section 5599:107512), and clicking anywhere advances to the next
// step. The only live elements are the two real MovmoCheckoutButton overlays,
// absolutely positioned at the exact coordinates the design places them
// (results bottom bar, node 5599:113569; Pasajeros banner, node 5599:113604).
// The Clic screens display 832.900,00 COP in Spanish; the Movmo overlay shows
// the same offer in English/USD — see buildMovmoFlightOfferForClic.

// Overlay geometry in percent of the 1792×1031 frame, from Figma node
// coordinates (button instances 5599:113759 at 1382,983 and 5599:113840 at
// 1063,160, both ~246×43 px).
const BUTTON_RESULTS = { left: "77.12%", top: "95.34%", width: "13.73%", height: "4.17%" };
const BUTTON_PASSENGERS = { left: "59.32%", top: "15.52%", width: "13.50%", height: "4.17%" };

// White button variant per the Clic design — kills the package's default pink
// gradient (`!bg-none`) and sizes the button to fill its overlay box. The
// badge restyle lives in ClicDemo.css (inner span, unreachable via className).
const CLIC_BUTTON_CLASSES =
    "!bg-none !bg-[#FAFAF9] !border !border-solid !border-[#D5D7DA] !rounded-lg " +
    "!text-[#525961] !font-semibold !shadow-sm " +
    "!w-full !h-full !p-0";

interface ScreenSpec {
    src: string;
    alt: string;
    results?: boolean; // the results frame is 1792×1050, the rest 1792×1031
    button?: { left: string; top: string; width: string; height: string };
}

const SCREENS: ScreenSpec[] = [
    { src: "/images/clic/01-home.png", alt: "Clic — home, BOG to CLO search" },
    { src: "/images/clic/02-results.png", alt: "Clic — flight results", results: true },
    { src: "/images/clic/03-fare-selected.png", alt: "Clic — ECONOMICA fare selected" },
    {
        src: "/images/clic/04-terms-accepted.png",
        alt: "Clic — fare conditions accepted",
        button: BUTTON_RESULTS,
    },
    {
        src: "/images/clic/05-passengers.png",
        alt: "Clic — passenger details form",
        button: BUTTON_PASSENGERS,
    },
];

const ClicDemo: React.FC = () => {
    // Persist ?mock=true early, same as AeroLanding. The Clic demo is
    // mock-only (there is no live KIU integration yet), so the button below
    // always targets the mock-clic provider regardless of the flag; calling
    // the hook keeps sessionStorage consistent for the rest of the app.
    useMockMode();
    const [screen, setScreen] = useState(0);

    // MOVMO-549: Handle page-level return from top-level redirect.
    useEffect(() => {
        consumeMovmoCheckoutReturn({
            isProd: false,
            onBookingComplete: () => {
                console.log("[clic-demo] booking complete");
            },
            onExit: ({ reason }: { reason: string }) => {
                console.log("[clic-demo] movmo checkout exit:", reason);
                // On Clic demo, always reset to passengers screen (like onExit in clic-vanilla.js)
                if (reason !== undefined) {
                    setScreen(SCREENS.length - 1);
                }
            },
        });
    }, []);

    // Preload the remaining screenshots so click-through never flashes white
    // mid-demo.
    useEffect(() => {
        SCREENS.slice(1).forEach(({ src }) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    const advance = useCallback(() => {
        setScreen((s) => Math.min(s + 1, SCREENS.length - 1));
    }, []);

    const current = SCREENS[screen];

    return (
        <div style={{ minHeight: "100vh", background: "#FAFAF9" }}>
            <div
                className={`clic-stage${current.results ? " clic-stage--results" : ""}`}
                onClick={advance}
                role="presentation"
            >
                <img src={current.src} alt={current.alt} draggable={false} />
                {current.button && (
                    <div
                        className="clic-express-btn"
                        style={current.button}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MovmoCheckoutButton
                            iataCode="VE"
                            apiKey={process.env.REACT_APP_MOVMO_API_KEY || ""}
                            providerID="mock-clic"
                            flightOffer={buildMovmoFlightOfferForClic()}
                            className={CLIC_BUTTON_CLASSES}
                            onExit={({ reason }) => {
                                // Land back on the passengers screen so the
                                // presenter can re-open checkout from either
                                // placement without restarting the flow.
                                console.log("[clic] movmo checkout exit:", reason);
                                setScreen(SCREENS.length - 1);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClicDemo;
